import { useState } from "react";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { AI_MODES, LANGUAGES } from "@/lib/constants";
import { LightningIcon, XIcon, LockSimpleIcon } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { hasPlan, openUpgrade } from "@/lib/plans";

// Reusable 8-AI-modes + Why It Matters panel (used on game pages).
export default function AIModesPanel({ gameId, defaultLanguage = "en" }) {
  const [language, setLanguage] = useState(defaultLanguage);
  const [output, setOutput] = useState(null);
  const [loadingMode, setLoadingMode] = useState(null);
  const { user } = useAuth();
  const isPro = hasPlan(user, "pro");

  const run = async (modeKey) => {
    if (modeKey !== "why_it_matters" && !isPro) {
      const label = AI_MODES.find((m) => m.key === modeKey)?.label || "This AI mode";
      openUpgrade({ code: "upgrade_required", required_plan: "pro", message: `${label} is part of Pro Fan.` });
      return;
    }
    setLoadingMode(modeKey);
    setOutput(null);
    try {
      const endpoint = modeKey === "why_it_matters" ? `/games/${gameId}/why-it-matters` : `/games/${gameId}/ai-mode`;
      const body = modeKey === "why_it_matters" ? { language } : { mode: modeKey, language };
      const res = await api.post(endpoint, body);
      const label = AI_MODES.find((m) => m.key === modeKey)?.label || "Gamly";
      setOutput({ mode: modeKey, label, text: res.data.text });
    } catch (err) {
      captureError(`ai mode ${modeKey} failed:`, err);
      if (err?.response?.status === 402) return;
      setOutput({ mode: modeKey, label: "Error", text: "Could not generate. Try again." });
    } finally {
      setLoadingMode(null);
    }
  };

  return (
    <div data-testid="ai-modes-panel">
      <div className="flex items-center gap-2 mb-3">
        <select
          data-testid="lang-select"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="bg-black border border-white/15 text-white/80 text-xs uppercase font-bold tracking-wider rounded-md px-3 py-2"
        >
          {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
        <button
          onClick={() => run("why_it_matters")}
          disabled={!!loadingMode}
          data-testid="live-why-btn"
          className="flex-1 py-3 rounded-md bg-white/5 border border-[#0A84FF]/60 text-[#0A84FF] hover:bg-[#0A84FF] hover:text-white font-black text-base uppercase tracking-wide transition-colors inline-flex items-center justify-center gap-2"
        >
          <LightningIcon weight="fill" /> {loadingMode === "why_it_matters" ? "Generating…" : "Why It Matters"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {AI_MODES.map((m) => (
          <button
            key={m.key}
            onClick={() => run(m.key)}
            disabled={!!loadingMode}
            data-testid={`live-mode-${m.key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[11px] font-bold uppercase tracking-wider transition-colors ${
              loadingMode === m.key
                ? "border-[#0A84FF] bg-[#0A84FF]/10 text-[#0A84FF]"
                : "border-white/10 text-white/70 hover:text-white hover:border-white/25"
            }`}
          >
            {!isPro && m.key !== "why_it_matters" ? <LockSimpleIcon weight="fill" size={12} className="text-[#FDB927]" data-testid={`live-mode-lock-${m.key}`} /> : <m.icon weight="bold" size={12} />}
            {m.label}
          </button>
        ))}
      </div>

      {loadingMode && (
        <div className="p-4 rounded-md bg-[#121212] border border-white/10 text-sm text-white/70">
          <p className="font-display tracking-wider">Gamly thinking · {AI_MODES.find((m) => m.key === loadingMode)?.label}…</p>
        </div>
      )}

      {output && (
        <div className="p-5 rounded-md bg-[#121212] border border-[#32D74B]/30" data-testid="live-why-output">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#32D74B]">{output.label}</p>
            <button onClick={() => setOutput(null)} className="text-white/40 hover:text-white" data-testid="live-close-output"><XIcon size={14} /></button>
          </div>
          <pre className="whitespace-pre-wrap font-medium text-sm text-white/90 leading-relaxed font-sans">{output.text}</pre>
        </div>
      )}
    </div>
  );
}
